import React, { useCallback, useEffect, useState } from "react";

import classNames from "classnames";
import nlp from "compromise";
import plg, { DatesMethods } from "compromise-dates";
import moment from "moment";
import styled from "styled-components";

import { InputMode, InputSize } from "./types";
import { isValidTime } from "../../utils/timeUtils";
import { ComboBox } from "../ComboBox/ComboBox";
import { IOptionItemProps } from "../Select/Option";
import { Time24HourFormat } from "../SelectTime";

nlp.plugin(plg);

export const StyledInputTimeWrapper = styled.div`
  max-width: 176px;
  flex: 1;

  & .c-combo-box--list {
    min-width: 176px;
  }
`;

export interface InputTimeProps {
  mode?: "12" | "24";
  selected?: Time24HourFormat;
  min?: Time24HourFormat;
  max?: Time24HourFormat;
  onChange?: (val: Time24HourFormat) => void;
  className?: string;
  placeholder?: string;
  size?: InputSize;
  step?: 1 | 5 | 15 | 30 | 60;
  triggerMode?: InputMode;
  disabled?: boolean;
  invalid?: boolean;
}

const renderOption = (option) => {
  return <div id={option.id}>{option.name}</div>;
};

export const InputTime: React.FC<InputTimeProps> = ({
  mode = "24",
  selected: defaultSelected,
  min = "00:00",
  max = "23:45",
  onChange,
  step = 15,
  size,
  className,
  placeholder,
  triggerMode,
  disabled,
  invalid,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const generateTimeOptions = (): IOptionItemProps[] => {
    const minTime = moment(isValidTime(min) ? min : "00:00", "HH:mm");
    const maxTime = moment(isValidTime(max) ? max : "23:45", "HH:mm");
    const allOptions: IOptionItemProps[] = [];

    const currentTime = moment("00:00", "HH:mm");

    while (currentTime.isBefore(moment("24:00", "HH:mm"))) {
      allOptions.push({
        id: currentTime.format("HH:mm"),
        name: currentTime.format(mode === "12" ? "hh:mm A" : "HH:mm"),
      });
      currentTime.add(step, "minutes");
    }

    return allOptions.filter(
      (option) =>
        moment(option.id, "HH:mm").isSameOrAfter(minTime) &&
        moment(option.id, "HH:mm").isSameOrBefore(maxTime)
    );
  };

  const timeOptions = generateTimeOptions();

  const isValidDefaultSelected = defaultSelected
    ? timeOptions.some((opt) => opt.id === defaultSelected)
    : false;

  const [selected, setSelected] = useState<Time24HourFormat | undefined>(
    isValidDefaultSelected ? defaultSelected : undefined
  );

  const handleInputChange = useCallback(
    (text: string) => {
      if (/\d{1,2}:\d{2}/.test(text.trim())) {
        return;
      }
      const doc = nlp<DatesMethods>(text);
      const parsed = doc.times().get()[0];
      if (parsed && parsed["24h"] && parsed["24h"] !== selected) {
        const value = moment(parsed).format("HH:mm") as Time24HourFormat;
        if (timeOptions.some((option) => option.id === value)) {
          setSelected(value);
          onChange?.(value);
          setIsOpen(false);
        }
      }
    },
    [onChange, selected, timeOptions]
  );

  const handleChange = (e) => {
    setSelected(e);
    onChange?.(e);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && selected) {
      const selectedItem = document.getElementById(selected)?.parentElement;
      selectedItem?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isOpen, selected]);

  useEffect(() => {
    if (
      defaultSelected !== selected &&
      timeOptions.some((opt) => opt.id === defaultSelected)
    )
      setSelected(defaultSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected, timeOptions]);

  return (
    <StyledInputTimeWrapper className={classNames("c-input-time", className)}>
      <ComboBox
        {...rest}
        options={timeOptions}
        open={isOpen}
        disabledInternalSort
        selected={selected}
        keepSameOptionsOrder
        onInputChange={handleInputChange}
        onChange={handleChange}
        forceCloseMenu
        placeholder={placeholder}
        renderOption={renderOption}
        autoHeightMax={226}
        size={size}
        onClose={handleClose}
        onOpen={handleOpen}
        triggerMode={triggerMode}
        disabled={disabled}
        invalid={invalid}
      />
    </StyledInputTimeWrapper>
  );
};
